import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useTheme, useMediaQuery } from '@mui/material';

ChartJS.register(...registerables, ChartDataLabels);

const UserGrowthChart = ({ data = [] }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Default data if none provided
    const defaultData = [
        { label: 'Jan', users: 0 },
        { label: 'Feb', users: 50 },
        { label: 'Mar', users: 100 },
        { label: 'Apr', users: 500 },
        { label: 'May', users: 750 },
        { label: 'Jun', users: 900 },
        { label: 'Jul', users: 1000 },
        { label: 'Aug', users: 1500 },
        { label: 'Sep', users: 1800 },
        { label: 'Oct', users: 1900 },
        { label: 'Nov', users: 2450 },
        { label: 'Dec', users: 0 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.label);
    const values = chartData.map(item => item.users);
    const maxValue = Math.max(...values, 100);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const fontSize = isMobile ? 10 : isTablet ? 11 : 12;
            const labelFontSize = isMobile ? 11 : isTablet ? 12 : 14;
            const pointRadius = isMobile ? 3 : isTablet ? 3.5 : 4;
            const pointHoverRadius = isMobile ? 5 : isTablet ? 5.5 : 6;
            const tooltipPadding = isMobile ? 8 : 10;

            const ctx = chartRef.current.getContext('2d');
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, '#E0F5FFB2');
            gradient.addColorStop(1, 'rgba(224, 245, 255, 0)');

            // Calculate appropriate step size based on max value
            const stepSize = Math.ceil(maxValue / 5 / 100) * 100 || 100;

            chartInstance.current = new ChartJS(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Users',
                            data: values,
                            borderColor: '#0C597D',
                            backgroundColor: gradient,
                            borderWidth: isMobile ? 1.5 : 2,
                            fill: true,
                            tension: 0.1,
                            pointRadius: pointRadius,
                            pointBackgroundColor: '#fff',
                            pointBorderColor: '#0C597D',
                            pointBorderWidth: isMobile ? 1.5 : 2,
                            pointHoverRadius: pointHoverRadius,
                            pointHoverBackgroundColor: '#0C597D',
                            pointHoverBorderColor: '#fff',
                            pointHoverBorderWidth: isMobile ? 1.5 : 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            titleColor: '#333',
                            bodyColor: '#333',
                            borderColor: '#0C597D',
                            borderWidth: 1,
                            padding: tooltipPadding,
                            displayColors: false,
                            titleFont: { size: isMobile ? 11 : 12 },
                            bodyFont: { size: isMobile ? 11 : 12 },
                            callbacks: {
                                label: (context) => `Users: ${context.raw.toLocaleString()}`
                            }
                        },
                        datalabels: {
                            display: (context) => context.active,
                            align: 'top',
                            anchor: 'end',
                            offset: isMobile ? 4 : isTablet ? 6 : 8,
                            color: '#0C597D',
                            font: {
                                family: '"Inter", sans-serif',
                                size: labelFontSize,
                                weight: '700',
                            },
                            formatter: (value) => value.toLocaleString(),
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: {
                                color: '#222222',
                                font: { size: fontSize },
                                maxRotation: isMobile ? 45 : 0,
                                minRotation: isMobile ? 45 : 0,
                            },
                        },
                        y: {
                            min: 0,
                            max: Math.ceil(maxValue * 1.2 / stepSize) * stepSize,
                            ticks: {
                                stepSize: stepSize,
                                color: '#131010ff',
                                font: { size: fontSize },
                                maxTicksLimit: isMobile ? 5 : 10,
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, isTablet, data, labels, values, maxValue]);

    return <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />;
};

export default UserGrowthChart;
